import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { SCROLL_RESTORATION_ROUTES } from "~/constants/scrollRestorationRoutes";
import {
  SCROLL_PERSIST_SUFFIX,
  clampScrollTop,
  clearScrollState,
  getMainScrollElement,
  getScrollStorageKey,
  matchListRoute,
  readScrollState,
  resolveListScopeForPathname,
  restoreScrollForScope,
  writeScrollState,
} from "~/utils/scrollRestoration";

const initialState = { x: 0, y: 0, index: 0, isVirtualList: false };

const useScrollPersist = (page) => {
  const location = useLocation();
  const scopeRef = useRef(page || null);
  const [scroll, setScroll] = useState(initialState);

  const resolveActiveScope = useCallback(
    (explicitScope) => {
      if (explicitScope) return explicitScope;
      if (scopeRef.current) return scopeRef.current;
      if (page) return page;
      return resolveListScopeForPathname(
        location.pathname,
        SCROLL_RESTORATION_ROUTES,
      );
    },
    [page, location.pathname],
  );

  const getScope = (sc) => getScrollStorageKey(sc);

  const setScope = (sc) => {
    scopeRef.current = sc;
  };

  const getScopeData = (sc) => {
    const scope = resolveActiveScope(sc);
    if (!scope) return null;
    return readScrollState(scope);
  };

  const persistScrollState = useCallback(
    (pos, sc) => {
      const scope = resolveActiveScope(sc);
      if (!scope) return;
      writeScrollState(scope, pos);
      setScroll(pos);
    },
    [resolveActiveScope],
  );

  const setScopeData = (pos) => {
    const position = typeof pos === "string" ? JSON.parse(pos) : pos;
    persistScrollState(position);
  };

  const setVListPos = (index, scrollTop = null, rowHeight = 0) => {
    const top =
      typeof scrollTop === "number"
        ? scrollTop
        : Math.max(0, index) * rowHeight;
    persistScrollState({
      index,
      x: 0,
      y: top,
      scrollTop: top,
      isVirtualList: true,
      container: "virtual-list",
    });
  };

  const captureMainScroll = useCallback(
    (sc, extra = {}) => {
      const main = getMainScrollElement();
      if (!main) return;
      const scope = resolveActiveScope(sc);
      const existing = scope ? readScrollState(scope) : null;
      persistScrollState(
        {
          ...(existing || {}),
          ...extra,
          x: main.scrollLeft,
          y: main.scrollTop,
          index: 0,
          isVirtualList: false,
          container: "main",
        },
        sc,
      );
    },
    [persistScrollState, resolveActiveScope],
  );

  const captureElementScroll = useCallback(
    (element, sc, extra = {}) => {
      if (!element) return;
      persistScrollState(
        {
          x: element.scrollLeft,
          y: element.scrollTop,
          index: 0,
          isVirtualList: false,
          container: "element",
          ...extra,
        },
        sc,
      );
    },
    [persistScrollState],
  );

  const restoreScroll = useCallback(
    (sc, element) => {
      const scope = sc || page;
      if (!scope) return;
      const saved = readScrollState(scope);
      if (!saved) return;

      setScroll(saved);

      if (saved.isVirtualList) {
        return saved;
      }

      const target =
        element || (saved.container === "main" ? getMainScrollElement() : null);

      if (target) {
        const top = clampScrollTop(target, saved.y ?? 0);
        const left = Math.max(0, saved.x ?? 0);
        target.scrollTo({ top, left });
      } else {
        window.scrollTo(saved.x ?? 0, saved.y ?? 0);
      }

      return saved;
    },
    [page],
  );

  const clearScopeScroll = (scopePage) => {
    const sc = scopePage || page;
    if (sc) clearScrollState(sc);
  };

  const clearAll = () => {
    for (const key in sessionStorage) {
      if (key.includes(SCROLL_PERSIST_SUFFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  };

  useLayoutEffect(() => {
    if (!page) return;
    scopeRef.current = page;

    const listRoute = matchListRoute(
      location.pathname,
      SCROLL_RESTORATION_ROUTES,
    );
    if (!listRoute) return;

    const saved = readScrollState(page) || initialState;
    setScroll(saved);
    if (saved?.isVirtualList) return;

    restoreScrollForScope(page);
  }, [page, location.pathname]);

  return {
    scroll,
    getScopeData,
    setScopeData,
    setVListPos,
    clearAll,
    clearScopeScroll,
    getScope,
    setScope,
    captureMainScroll,
    captureElementScroll,
    restoreScroll,
    persistScrollState,
  };
};

export default useScrollPersist;
